import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from './Icon';
import '../css/sort-menu.css';

const OrderLogsSortFiltersMenu = ({
  sort,
  direction,
  onSortChange,
  onDirectionChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortOptions = [
    { value: 'createdAt', label: t('home.orderLogs.filters.sortByCreatedDate') },
    { value: 'updatedAt', label: t('home.orderLogs.filters.sortByUpdatedDate') },
    { value: 'attemptsCount', label: t('home.orderLogs.filters.sortByAttempts') },
  ];

  const directionOptions = [
    { value: 'DESC', label: t('orders.filters.descending'), icon: '↓' },
    { value: 'ASC', label: t('orders.filters.ascending'), icon: '↑' },
  ];

  const isSortMenuActive = useMemo(() => {
    return direction !== 'DESC' || sort !== 'createdAt';
  }, [direction, sort]);

  return (
    <div className="sort-menu-wrapper orders-sort-filters-wrapper" ref={menuRef}>
      <button
        type="button"
        className={`sort-menu-button${isSortMenuActive ? ' filter-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('orders.filters.sortButton')}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t('orders.filters.sortButton')}</span>
        <span className="sort-menu-arrow">
          <Icon
            name="chevron-down"
            size={12}
            className={`expand-icon ${isOpen ? 'open' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sort-menu-dropdown orders-sort-filters-dropdown"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="orders-sort-main-grid">
              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t('orders.filters.sortBy')}</div>
                <div className="sort-menu-options">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                      onClick={() => onSortChange && onSortChange(option.value)}
                    >
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t('orders.filters.sortDirection')}</div>
                <div className="sort-menu-options">
                  {directionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                      onClick={() => onDirectionChange && onDirectionChange(option.value)}
                    >
                      <span className="sort-menu-option-icon">{option.icon}</span>
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderLogsSortFiltersMenu;
